import { useAppSelector } from '../hooks';
import useAccessControl from '../hooks/access-control/useAccessControl';
import { EMenuItems } from './menu.slice';

type AccessConfig = Record<
  EMenuItems,
  {
    hasAccess: boolean;
    hasDisplayBadgeContent?: string | null;
  }
>;

const useMainMenuAccessConfig = (): AccessConfig => {
  const {
    canAccessIncidents,
    canAccessPorts,
    canAccessRoutes,
    canAccessHistory,
    canAccessRiMaritimeAreas,
    canAccessBoundaries,
    canAccessDrawings,
    canAccessDocuments,
    canAccessUserPreferences,
    canAccessMaps,
    canAccessMapTools,
    canAccessFeatureMyFleet,
    canAccessMyFleet,
    canAccessVPS,
    canAccessFeatureProFeatureBadge,
    canAccessIranConflict,
    canAccessFeatureIranConflictPanel,
  } = useAccessControl();

  const focusRegionActive = useAppSelector((state) => state.focusRegion.active);

  // When conflict mode is active, gate items that aren't relevant to the conflict view.
  // Panels with conflict-zone filtering (incidents, ports, correspondents, news, RI areas)
  // remain accessible so the user can explore the filtered data sets.
  const focusRegionGate = (hasAccess: boolean) =>
    focusRegionActive ? false : hasAccess;

  return {
    [EMenuItems.HISTORY]: {
      hasAccess: focusRegionGate(canAccessHistory),
    },
    [EMenuItems.MAPS]: {
      hasAccess: focusRegionGate(canAccessMaps),
    },
    [EMenuItems.INCIDENTS]: {
      // Accessible during conflict — incidents are geo-filtered to the zone
      hasAccess: canAccessIncidents,
    },
    [EMenuItems.BOUNDARIES]: {
      hasAccess: focusRegionGate(canAccessBoundaries),
    },
    [EMenuItems.PORTS]: {
      // Accessible during conflict — port list is filtered to the zone
      hasAccess: Object.values(canAccessPorts).some(
        (portTypeAccess) => portTypeAccess === true
      ),
    },
    [EMenuItems.DRAWINGS]: {
      hasAccess: focusRegionGate(canAccessDrawings),
    },
    [EMenuItems.ROUTES]: {
      hasAccess: focusRegionGate(canAccessRoutes),
    },
    [EMenuItems.RI_MARITIME_AREAS]: {
      // Accessible during conflict — areas are filtered to the four conflict seas
      hasAccess: canAccessRiMaritimeAreas,
    },
    [EMenuItems.DOCUMENTS]: { hasAccess: focusRegionGate(canAccessDocuments) },
    [EMenuItems.SAVE_SETUP]: {
      hasAccess: focusRegionGate(canAccessUserPreferences),
    },
    [EMenuItems.PRIVACY_POLICY]: { hasAccess: focusRegionGate(true) },
    [EMenuItems.COUNTRIES]: { hasAccess: focusRegionGate(true) },
    [EMenuItems.TOOLS]: { hasAccess: focusRegionGate(canAccessMapTools) },
    [EMenuItems.INDUSTRY_NEWS]: {
      // Accessible during conflict — news is filtered to the zone
      hasAccess: true,
    },
    [EMenuItems.VPS_PORTS]: {
      hasAccess: focusRegionGate(canAccessVPS),
    },
    [EMenuItems.NOTHING_SELECTED]: { hasAccess: true },
    [EMenuItems.FIRST_CALL_PORTS]: { hasAccess: focusRegionGate(true) },
    [EMenuItems.CORRESPONDENTS]: {
      // Accessible during conflict — correspondents are filtered to the zone
      hasAccess: true,
    },
    [EMenuItems.MY_FLEET]: {
      hasAccess:
        focusRegionGate(
          (canAccessMyFleet && canAccessFeatureMyFleet.enabled) || false
        ) || false,
      hasDisplayBadgeContent: canAccessFeatureProFeatureBadge.enabled
        ? canAccessFeatureProFeatureBadge.content
        : null,
    },
    // Iran Conflict: never gated by focusRegionGate (it is the conflict item itself)
    [EMenuItems.IRAN_CONFLICT]: {
      hasAccess:
        (canAccessIranConflict && canAccessFeatureIranConflictPanel.enabled) ||
        false,
      // Badge text comes from the Iran Conflict flag's own badgeContent field
      hasDisplayBadgeContent: canAccessFeatureIranConflictPanel.enabled
        ? canAccessFeatureIranConflictPanel.badgeContent
        : null,
    },
  };
};

export default useMainMenuAccessConfig;
